import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import Modal from "components/base/Modal";
import CampaignModalAutoDropdown from "components/SearchDropdown/CampaignModalAutoDropdown";
import ListManagementField from "../../../components/ListManagementField";
import { selectCampaigns } from "data/campaigns/selectors";
import { saveCampaign } from "data/campaigns/actions";
import css from "./style.scss";

export const CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT = {
    CAMPAIGN: "CAMPAIGN",
    MY_PROPERTY: "MY_PROPERTY",
};

export const CAMPAIGN_ACTIVITY_SETTINGS_MODE = CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT;


export const CAMPAIGN_ACTIVITY_TYPE = {
    DIALER: "DIALER",
    POSTCARD: "POSTCARD",
    EMAIL: "EMAIL",
    DIRECT_MAIL: "DIRECT MAIL",
};

function resolveContext(context) {
    if (
        context === CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT.MY_PROPERTY ||
        context === "MY_PROPERTIES"
    ) {
        return CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT.MY_PROPERTY;
    }
    return CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT.CAMPAIGN;
}

function parseCampaignIdForSave(raw) {
    if (raw == null || raw === "") {
        return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

const CAMPAIGN_TAB_TITLE_MAP = {
    [CAMPAIGN_ACTIVITY_TYPE.DIALER]: "Dialer Activity Name",
    [CAMPAIGN_ACTIVITY_TYPE.POSTCARD]: "Postcard Activity Name",
    [CAMPAIGN_ACTIVITY_TYPE.EMAIL]: "Email Activity Name",
};

const ACTIVITY_NAME_LABEL_MAP = {
    [CAMPAIGN_ACTIVITY_TYPE.DIALER]: "New Dialer Activity Name",
    [CAMPAIGN_ACTIVITY_TYPE.POSTCARD]: "New Postcard Activity Name",
    [CAMPAIGN_ACTIVITY_TYPE.EMAIL]: "New Email Activity Name",
};

const CampaignActivitySettingsModal = ({
    isOpen,
    onClose,
    activityType,
    context,
    campaignId,
    selectionContactIds = [],
    onSave,
    campaigns,
    dispatchSaveCampaign,
    customClassName,
}) => {
    const [activityName, setActivityName] = useState("");
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [pendingCampaignQuery, setPendingCampaignQuery] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dropdownKey, setDropdownKey] = useState(0);

    const resolvedContext = resolveContext(context);
    const isMyPropertyMode =
        resolvedContext === CAMPAIGN_ACTIVITY_SETTINGS_CONTEXT.MY_PROPERTY;

    const title = isMyPropertyMode
        ? "Campaign Activity Settings"
        : CAMPAIGN_TAB_TITLE_MAP[activityType] || "New Activity";

    const activityNameLabel =
        ACTIVITY_NAME_LABEL_MAP[activityType] || "New Activity Name";

    const activeCampaigns = campaigns
        ? campaigns
            .filter((c) => c.get("active") === true)
            .map((c) => ({ id: c.get("id"), name: c.get("name") }))
            .toArray()
        : [];

    useEffect(() => {
        if (!isOpen) return;
        setActivityName("");
        setSelectedCampaign(null);
        setPendingCampaignQuery("");
        setError(null);
        setIsLoading(false);
        setDropdownKey((k) => k + 1);
    }, [isOpen]);

    const handleSelect = useCallback(
        (item) => {
            if (!item) {
                setSelectedCampaign(null);
                return;
            }

            const match =
                item.id != null && activeCampaigns.find((c) => c.id === item.id);

            if (match) {
                setSelectedCampaign(match);
            } else {
                setSelectedCampaign({ name: item.name, id: undefined });
            }
        },
        [activeCampaigns],
    );

    const resolveMyPropertiesMarketing = useCallback(
        () => selectedCampaign,
        [selectedCampaign],
    );

    const handleCancel = useCallback(() => {
        setDropdownKey((k) => k + 1);
        onClose();
    }, [onClose]);

    const handleSave = async () => {
        const isDirectMail =
            activityType === CAMPAIGN_ACTIVITY_TYPE.DIRECT_MAIL;

        if (!isDirectMail && !activityName) {
            return;
        }

        setIsLoading(true);
        setError(null);

        let marketingCampaignId = null;

        try {
            if (!isMyPropertyMode) {
                marketingCampaignId = parseCampaignIdForSave(campaignId);
                if (marketingCampaignId == null) {
                    setError("Missing marketing campaign. Please reopen this dialog from a campaign.");
                    setIsLoading(false);
                    return;
                }
            } else {
                const selection = resolveMyPropertiesMarketing();
                if (!selection?.name?.trim()) {
                    setError("Please select or enter a marketing campaign.");
                    setIsLoading(false);
                    return;
                }

                if (selection.id != null) {
                    marketingCampaignId = selection.id;
                } else {
                    const ids = Array.isArray(selectionContactIds)
                        ? selectionContactIds
                        : [];
                    const response = await dispatchSaveCampaign({
                        name: selection.name.trim(),
                        contactIds: ids,
                    });
                    marketingCampaignId = response.campaign.id;
                }
            }

           await onSave(activityName, marketingCampaignId);
        } catch (err) {
            setError("Failed to create campaign. Please try again.");
            setIsLoading(false);
        }
    };

    const myPropertyFieldsReady =
        !isMyPropertyMode ||
        Boolean(resolveMyPropertiesMarketing()?.name?.trim());

    const campaignModeReady =
        isMyPropertyMode || parseCampaignIdForSave(campaignId) != null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleCancel}
            uniqId="CampaignActivitySettings"
            closeOnOutsideClick={false}
            modalClassName={`${css.campaignActivitySettingsModal} ${customClassName || ''}`}
            width={isMyPropertyMode ? "492px" : "433px"}
            height="auto"
            maxWidth="calc(100vw - 48px)"
            maxHeight="calc(100vh - 48px)"
            padding="30px 37px"
            background="#EFF2F6"
            border="none"
        >
            <div className={css.modalBody}>
                <div className={css.modalHeader}>
                    <div
                        className={
                            isMyPropertyMode
                                ? css.modalTitleMyProperties
                                : css.modalTitleCampaignTab
                        }
                    >
                        {title}
                    </div>
                </div>

                {isMyPropertyMode && (
                    <div className={css.fieldGroup}>
                        <ListManagementField
                            key={dropdownKey}
                            lists={activeCampaigns}
                            label="Add to Marketing Campaign"
                            placeholder="Select or type to create a new campaign"
                            onSelect={handleSelect}
                            onCreate={(name) => {
                                handleSelect({ name, id: undefined });
                            }}
                            onSearchChange={setPendingCampaignQuery}
                            value={selectedCampaign}
                            inputWrapperClassName={css.comboBox44}
                            inputClassName={css.comboBox44Input}
                            arrowButtonClassName={css.comboBoxArrow}
                            createLabel="(Create as New Campaign)"
                        />
                    </div>
                )}
                {activityType !== CAMPAIGN_ACTIVITY_TYPE.DIRECT_MAIL && (
                    <div className={css.fieldGroup}>
                        {isMyPropertyMode && (
                            <label className={css.fieldLabel} htmlFor="campaign-activity-name-input">
                                {activityNameLabel}
                            </label>
                        )}
                        <input
                            id="campaign-activity-name-input"
                            className={css.activityNameInput}
                            type="text"
                            placeholder="Enter name"
                            value={activityName}
                            onChange={(e) => setActivityName(e.target.value)}
                        />
                    </div>
                )}
                {error && <div className={css.error}>{error}</div>}

                <div className={css.footer}>
                    <button
                        type="button"
                        className={css.cancelButton}
                        disabled={isLoading}
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={css.saveButton}
                        disabled={
                            // !activityName.trim() ||
                            (
                                activityType !== CAMPAIGN_ACTIVITY_TYPE.DIRECT_MAIL &&
                                !activityName.trim()
                            ) ||
                            isLoading ||
                            !myPropertyFieldsReady ||
                            !campaignModeReady
                        }
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </div>
        </Modal >
    );
};

const mapStateToProps = (state) => ({
    campaigns: selectCampaigns(state),
});

const mapDispatchToProps = (dispatch) => ({
    dispatchSaveCampaign: (campaign) =>
        new Promise((resolve, reject) => {
            dispatch(
                saveCampaign(
                    campaign,
                    ({ response }) => resolve(response),
                    () => reject(new Error("SAVE_CAMPAIGN_ERROR")),
                ),
            );
        }),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(CampaignActivitySettingsModal);
